"use client";

import { useEffect } from "react";
import { useConfigurator } from "@/store/configurator";
import { Fretboard } from "@/components/configurator/Fretboard";
import { ConfiguratorFooter } from "@/components/configurator/ConfiguratorFooter";
import { chipStyle } from "@/components/configurator/chip-style";
import { GaugeInfo } from "@/components/common/GaugeInfo";
import { gaugeOptionsFor } from "@/lib/configurator/gauge-options";
import { inferWindingByPosition } from "@/lib/catalog/gauge-format";
import { useConfiguratorPrice } from "@/hooks/useConfiguratorPrice";
import { formatPrice } from "@/lib/utils";
import type { ConfiguratorString, WindingType } from "@/types/configurator";

/** "10–46" style range from an ordered gauge list (inch decimals → thousandths). */
function gaugeRange(gauges: { gaugeValue: string }[]): string | null {
  if (gauges.length < 2) return null;
  const thou = (v: string) => Math.round(parseFloat(v) * 1000);
  const first = thou(gauges[0].gaugeValue);
  const last = thou(gauges[gauges.length - 1].gaugeValue);
  if (!Number.isFinite(first) || !Number.isFinite(last)) return null;
  return `${first}–${last}`;
}

interface Props { onNext: () => void; onBack: () => void; }

export function GaugeStep({ onNext, onBack }: Props) {
  const { state, referenceData, setPreset, setStrings } = useConfigurator();

  const instrumentId = state.instrumentId ?? "";
  const lineId = state.lineId ?? "";
  const stringCount = state.stringCount ?? 6;

  const instrument = referenceData.instruments.find((entry) => entry.slug === instrumentId);
  const category = instrument?.category ?? "electric";
  const line = referenceData.lines.find((entry) => entry.slug === lineId);
  // Presets are generated per string type, because manufacturing capability —
  // and therefore construction — is recorded per type. Showing another type's
  // sets displayed the wrong construction and produced quotes the server
  // refused.
  const stringTypeId = state.stringTypeId;
  const instrumentPresets = (line?.presets ?? []).filter(
    (preset) => preset.instrumentSlug === instrumentId && preset.stringTypeSlug === stringTypeId
  );
  // Scope to the chosen string count (e.g. a 4-string bass never sees 5/6-string
  // sets). No match means this line carries no set for that count — Prime on a
  // 7-string electric, say — so drop to the custom-gauge path below rather than
  // truncating a set built for a different number of strings.
  const presets = instrumentPresets.filter((preset) => preset.strings.length === stringCount);

  // Selection derives from the store so it survives leaving and re-entering the step.
  const selectedPreset = presets.find((preset) => preset.id === state.presetId) ?? null;

  // Initialize strings from first preset if none set
  useEffect(() => {
    if (state.strings.length === 0 && presets.length > 0) {
      const first = presets[0];
      applyPreset(first.id, first.strings.slice(0, stringCount), first.name);
    }
    if (state.strings.length === 0 && presets.length === 0 && stringCount > 0) {
      // No preset for this combo: seed a plausible curve from the curated
      // per-position options (first value of each column is the lightest sane gauge).
      const blankStrings: ConfiguratorString[] = Array.from({ length: stringCount }, (_, index) => ({
        position: index + 1,
        gaugeValue: gaugeOptionsFor(category, stringCount, index)[0] ?? "0.010",
        windingType: inferWindingByPosition(category, index),
        colorId: null,
        scaleLengthId: null,
      }));
      // A custom build, not a preset. This used to dispatch a synthetic
      // "<instrument>-<line>-custom" id, which the server then failed to find
      // as a real preset and rejected — blocking every non-standard build.
      setStrings(blankStrings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrumentId, lineId, stringTypeId, stringCount, presets.length]);

  function applyPreset(
    presetId: string,
    gauges: { position: number; gaugeValue: string; windingType: WindingType }[],
    presetName: string
  ) {
    const strings: ConfiguratorString[] = gauges.slice(0, stringCount).map((g, i) => ({
      position: i + 1,
      gaugeValue: g.gaugeValue,
      windingType: g.windingType,
      colorId: null,
      scaleLengthId: null,
    }));
    setPreset(presetId, strings, presetName);
  }

  const canProceed = state.strings.length > 0;

  return (
    <div>
      <h2 className="text-2xl font-black mb-2" style={{ fontFamily: "var(--font-display)" }}>
        Build your strings.
      </h2>
      <p className="text-sm mb-6 flex items-center gap-2" style={{ color: "#9A9A9A" }}>
        Pick a feel, then tap any string to fine-tune it. <GaugeInfo />
      </p>

      {/* Preset selector */}
      {presets.length > 0 && (
        <div className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: "#8A8A8A" }}>
            Start from preset
          </p>
          <div className="flex flex-wrap gap-2">
            {presets.map((p) => {
              const active = selectedPreset?.id === p.id;
              const range = gaugeRange(p.strings.slice(0, stringCount));
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => applyPreset(p.id, p.strings, p.name)}
                  aria-pressed={active}
                  className="inline-flex items-baseline gap-2 px-4 py-2.5 text-sm font-medium rounded-sm border transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
                  style={chipStyle(active)}
                >
                  {p.name}
                  {range ? (
                    <span className="text-xs font-mono" style={{ color: active ? "#E8C547" : "#8A8A8A" }}>
                      {range}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Interactive fretboard */}
      {state.strings.length > 0 && <Fretboard mode="gauge" />}

      <CustomGaugeNote presetName={state.basePresetName} isCustom={state.isCustomGauge} />

      <ConfiguratorFooter onBack={onBack} onNext={onNext} nextDisabled={!canProceed} />
    </div>
  );
}

/** Quiet note when gauges deviate from the preset, with the surcharge amount
 *  named at the decision point instead of surfacing at Review. */
function CustomGaugeNote({ presetName, isCustom }: { presetName: string | null; isCustom: boolean }) {
  const { quote } = useConfiguratorPrice();
  if (!isCustom) return null;

  const surcharge = quote?.surcharges.find((s) => s.key === "CUSTOM_GAUGE");
  const surchargeLabel =
    surcharge && surcharge.amount > 0 ? ` · +${formatPrice(surcharge.amount)} per set` : "";

  return (
    <p
      className="text-sm mt-4 rounded-sm border px-4 py-3"
      role="status"
      style={{ backgroundColor: "#141414", borderColor: "#2A2A2A", color: "#C0C0C0" }}
    >
      <span className="font-semibold" style={{ color: "#E8C547" }}>
        {presetName ? `Modified from ${presetName}` : "Custom build"}
        {surchargeLabel}.
      </span>{" "}
      Custom gauges change feel and tension — double-check they suit your tuning.
    </p>
  );
}
